/**
 * Shared Subscribe Form Handler
 * Handles Mailchimp JSONP subscription forms
 */

export function initPluginSubscribe() {
  const forms = document.querySelectorAll('[data-subscribe-form]');

  forms.forEach((form) => {
    const card = form.closest('[data-subscribe-card]');
    const formState = card?.querySelector('[data-subscribe-form-state]');
    const successState = card?.querySelector('[data-subscribe-success-state]');
    const errorState = card?.querySelector('[data-subscribe-error-state]');
    const retryButton = card?.querySelector('[data-subscribe-retry]');
    const submitButton = form.querySelector('[data-subscribe-submit]');
    const emailInput = form.querySelector('[data-email-input]');
    const consentInput = form.querySelector('[data-consent-input]');
    const emailError = form.querySelector('[data-error="email"]');
    const consentError = form.querySelector('[data-error="consent"]');

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Clear error state
    function clearErrors() {
      emailError?.classList.add('hidden');
      consentError?.classList.add('hidden');
      emailInput?.classList.remove('!border-red-4');
      emailInput?.classList.add('border-cyan-300');
    }

    // Show specific error
    function showError(type) {
      if (type === 'email') {
        emailError?.classList.remove('hidden');
        emailInput?.classList.remove('border-cyan-300');
        emailInput?.classList.add('!border-red-4');
      } else if (type === 'consent') {
        consentError?.classList.remove('hidden');
      }
    }

    // Validate form
    function validateForm() {
      let isValid = true;
      clearErrors();

      // Validate email
      const emailValue = emailInput?.value.trim() || '';
      if (!emailValue || !emailRegex.test(emailValue)) {
        showError('email');
        isValid = false;
      }

      // Validate consent
      if (!consentInput?.checked) {
        showError('consent');
        isValid = false;
      }

      return isValid;
    }

    // Show success state
    function showSuccess() {
      formState?.classList.add('hidden');
      errorState?.classList.add('hidden');
      successState?.classList.remove('hidden');
      successState?.focus();
    }

    // Show error state
    function showErrorState(message) {
      const errorMessage = card?.querySelector('[data-subscribe-error-message]');
      if (errorMessage && message) {
        errorMessage.textContent = message;
      }
      formState?.classList.add('hidden');
      successState?.classList.add('hidden');
      errorState?.classList.remove('hidden');
      errorState?.focus();
    }

    // Reset to form state
    function resetToForm() {
      errorState?.classList.add('hidden');
      successState?.classList.add('hidden');
      formState?.classList.remove('hidden');
      clearErrors();
    }

    // Submit form via fetch (JSONP-style for Mailchimp)
    async function submitForm() {
      const formData = new FormData(form);
      const action = form.getAttribute('action');

      // Build URL with parameters for Mailchimp
      const params = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        params.append(key, value);
      }

      // Use Mailchimp's JSONP endpoint for cross-origin requests
      const jsonpUrl = action.replace('/post?', '/post-json?') + '&' + params.toString() + '&c=callback';

      return new Promise((resolve, reject) => {
        // Create unique callback name
        const callbackName = 'mailchimp_callback_' + Date.now();

        // Define the callback
        window[callbackName] = function(response) {
          // Clean up
          delete window[callbackName];
          script.remove();

          if (response.result === 'success') {
            resolve(response);
          } else if (response.result === 'error') {
            // Check if already subscribed (not really an error for us)
            if (response.msg && response.msg.includes('already subscribed')) {
              resolve(response);
            } else {
              reject(new Error(response.msg || 'Subscription failed'));
            }
          } else {
            reject(new Error('Unknown response'));
          }
        };

        // Create script element for JSONP
        const script = document.createElement('script');
        script.src = jsonpUrl.replace('&c=callback', '&c=' + callbackName);

        // Handle script errors
        script.onerror = function() {
          delete window[callbackName];
          script.remove();
          reject(new Error('Network error'));
        };

        // Set timeout
        const timeout = setTimeout(() => {
          if (window[callbackName]) {
            delete window[callbackName];
            script.remove();
            reject(new Error('Request timeout'));
          }
        }, 10000);

        // Override callback to clear timeout
        const originalCallback = window[callbackName];
        window[callbackName] = function(response) {
          clearTimeout(timeout);
          originalCallback(response);
        };

        document.body.appendChild(script);
      });
    }

    // Handle form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        // Focus first error field
        if (!emailRegex.test(emailInput?.value.trim() || '')) {
          emailInput?.focus();
        } else if (!consentInput?.checked) {
          consentInput?.focus();
        }
        return;
      }

      // Disable submit button
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Subscribing...';
      }

      try {
        await submitForm();
        showSuccess();
      } catch (error) {
        console.error('Subscription error:', error);
        showErrorState(error.message || 'Please try again later.');
      } finally {
        // Re-enable submit button
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = 'Subscribe';
        }
      }
    });

    // Handle retry button
    retryButton?.addEventListener('click', resetToForm);

    // Real-time validation on blur
    emailInput?.addEventListener('blur', () => {
      const emailValue = emailInput.value.trim();
      if (emailValue && !emailRegex.test(emailValue)) {
        showError('email');
      } else {
        emailError?.classList.add('hidden');
        emailInput?.classList.remove('!border-red-4');
        emailInput?.classList.add('border-cyan-300');
      }
    });

    // Clear email error on input
    emailInput?.addEventListener('input', () => {
      emailError?.classList.add('hidden');
      emailInput?.classList.remove('!border-red-4');
      emailInput?.classList.add('border-cyan-300');
    });

    // Clear consent error on change
    consentInput?.addEventListener('change', () => {
      if (consentInput.checked) {
        consentError?.classList.add('hidden');
      }
    });
  });
}
